import { diggingAjax } from '../ajax/DiggingAjax'
import { store } from '../state/store'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { DateTime } from 'luxon'

import { TruckLoadDTO } from '../DTOs/Production/TruckLoadDTO'
import type { TruckLoadMutationResultDTO } from '../DTOs/Production/TruckLoadMutationResultDTO'
import { computeShiftId } from './computeShiftId'
import { enqueue, queuedItemFor } from './queueOps'
import { nextTempId } from './tempId'
import { v4 as uuidv4 } from 'uuid'
import { isOnlineNow } from './connectivity'
import { addQueuedLoadToCachedDiggingSummary } from './patchCachedDiggingSummary'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

// Note: the shift the load belongs to, for the Production list's cached totals.
function currentShift(): { date: DateTime; shift: string } {
  const { selectedDate, selectedShift } = store.getState().shift
  return { date: DateTime.fromISO(selectedDate), shift: selectedShift }
}

export const loadingsMutations = {
  async insertTruckLoad(dto: TruckLoadDTO) {
    if (isOnlineNow()) return diggingAjax.insertTruckLoad(dto)
    const tempId = uuidv4()
    // Note: store the small negative temp id as entityId so a later offline edit/delete of this
    // still-unsynced load merges into this create (see queueOps dedup) rather than being sent as a
    // separate mutation carrying a temp loadingId.
    const tempLoadingId = await nextTempId()
    await enqueue({
      entity: 'loading',
      operation: 'create',
      shiftId: currentShiftId(),
      tempId,
      entityId: tempLoadingId,
      data: { ...dto },
    })
    const optimisticLoad = new TruckLoadDTO({ ...dto, loadingId: tempLoadingId })
    const { date, shift } = currentShift()
    addQueuedLoadToCachedDiggingSummary(dto, date, shift)
    return {
      success: true,
      data: { truckLoad: optimisticLoad, currentHourTotalLoads: 0, shiftTotalLoads: 0 } as TruckLoadMutationResultDTO | null,
    }
  },

  // Note: previous is the row as it stood before the edit, so the Production list's cached total can
  // move by the difference. Without it an edit went uncounted - a row taken from 0 to 10 loads left
  // the list reading 21 where the page read 31.
  // Note: the caller's copy comes from a render closure, which goes stale when the +/- buttons fire
  // several saves before a re-render - the total then moved by the wrong difference (four clicks on
  // one row each measured against the row's original value). The queue holds what this device last
  // wrote for the row, so prefer that as the baseline and fall back to the caller's copy for a row
  // this device has not touched yet.
  async updateTruckLoad(dto: TruckLoadDTO, previous?: { loads: number; tonnes?: number }) {
    if (isOnlineNow()) return diggingAjax.updateTruckLoad(dto)

    // Note: an update with no id cannot be applied by anyone - the server answers NotFound and the
    // crew gets a red line about a record that never existed. Refuse it here, where we can say so,
    // rather than queueing something certain to fail.
    if (!dto.loadingId) {
      console.error('Refusing to queue a truck load update with no loadingId', dto)
      return {
        success: false,
        data: null,
        errors: [{ code: 0, description: 'That load could not be saved offline because it has no id yet. Re-enter it and try again.' }],
      } as ActionResponse<TruckLoadMutationResultDTO | null>
    }
    const queued = await queuedItemFor('loading', dto.loadingId)
    const lastCounted = (queued?.data as { loads?: number; tonnes?: number } | undefined) ?? previous
    await enqueue({
      entity: 'loading',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: dto.loadingId,
      rowVersion: dto.rowVersion,
      data: { ...dto },
    })
    if (lastCounted) {
      const { date, shift } = currentShift()
      addQueuedLoadToCachedDiggingSummary({
        diggingPatternId: dto.diggingPatternId,
        loads: (dto.loads ?? 0) - (lastCounted.loads ?? 0),
        tonnes: (dto.tonnes ?? 0) - (lastCounted.tonnes ?? 0),
      }, date, shift)
    }
    return {
      success: true,
      data: { truckLoad: dto, currentHourTotalLoads: 0, shiftTotalLoads: 0 } as TruckLoadMutationResultDTO | null,
    }
  },

  // Note: the caller passes the row being removed so the Production list's cached totals can come
  // back down by the same amount they went up. Without it a load deleted offline left its count behind.
  async deleteTruckLoad(loadingId: number, rowVersion?: string, deleted?: { diggingPatternId: number; loads: number; tonnes?: number }) {
    if (isOnlineNow()) return diggingAjax.deleteTruckLoad(loadingId)
    await enqueue({
      entity: 'loading',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: loadingId,
      rowVersion,
    })
    if (deleted) {
      const { date, shift } = currentShift()
      addQueuedLoadToCachedDiggingSummary(deleted, date, shift, -1)
    }
    return {
      success: true,
      data: { truckLoad: null, currentHourTotalLoads: 0, shiftTotalLoads: 0 } as TruckLoadMutationResultDTO | null,
    }
  },
}
